import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import questionnaireService from 'services/questionnaireService';

const initialState = {
    templates: [],
    templatesLoading: false,
    templatesError: null,

    currentTemplate: null,
    currentTemplateLoading: false,
    currentTemplateError: null,

    submissions: [],
    submissionsLoading: false,
    submissionsError: null,

    statistics: null,
    statisticsLoading: false,
    statisticsError: null,

    comparison: null,
    comparisonLoading: false,
    comparisonError: null,

    createTemplateLoading: false,
    createTemplateError: null,

    sendQuestionnaireLoading: false,
    sendQuestionnaireError: null,

    schedulers: [],
    schedulersLoading: false,
    schedulerActionLoading: false,
};

export const getQuestionnaireTemplates = createAsyncThunk(
    'questionnaire/getTemplates',
    async (_, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getTemplates();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getQuestionnaireTemplate = createAsyncThunk(
    'questionnaire/getTemplate',
    async (templateId, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getTemplate(templateId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createQuestionnaireTemplate = createAsyncThunk(
    'questionnaire/createTemplate',
    async (data, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.createTemplate(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateQuestionnaireTemplate = createAsyncThunk(
    'questionnaire/updateTemplate',
    async ({ templateId, data }, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.updateTemplate(templateId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteQuestionnaireTemplate = createAsyncThunk(
    'questionnaire/deleteTemplate',
    async (templateId, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.deleteTemplate(templateId);
            return templateId;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const sendQuestionnaire = createAsyncThunk(
    'questionnaire/sendQuestionnaire',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.sendQuestionnaire(payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data);
        }
    }
);

export const getSubmissionsByDomain = createAsyncThunk(
    'questionnaire/getSubmissionsByDomain',
    async (domainId, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getSubmissionsByDomain(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getAllSubmissions = createAsyncThunk(
    'questionnaire/getAllSubmissions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getAllSubmissions();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getDomainStatistics = createAsyncThunk(
    'questionnaire/getDomainStatistics',
    async (domainId, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getDomainStatistics(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getSubmissionsComparison = createAsyncThunk(
    'questionnaire/getSubmissionsComparison',
    async (domainId, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getSubmissionsComparison(domainId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getSchedulersByTemplate = createAsyncThunk(
    'questionnaire/getSchedulersByTemplate',
    async (templateId, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.getSchedulersByTemplate(templateId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
 
export const updateQuestionnaireScheduler = createAsyncThunk(
    'questionnaire/updateScheduler',
    async ({ schedulerId, data }, { rejectWithValue }) => {
        try {
            const response = await questionnaireService.updateScheduler(schedulerId, data);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
 
export const deleteQuestionnaireScheduler = createAsyncThunk(
    'questionnaire/deleteScheduler',
    async (schedulerId, { rejectWithValue }) => {
        try {
            await questionnaireService.deleteScheduler(schedulerId);
            return schedulerId;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const questionnaireSlice = createSlice({
    name: 'questionnaire',
    initialState,
    reducers: {
        clearCurrentTemplate: (state) => {
            state.currentTemplate = null;
            state.currentTemplateError = null;
        },
        clearStatistics: (state) => {
            state.statistics = null;
            state.statisticsError = null;
        },
        clearComparison: (state) => {
            state.comparison = null;
            state.comparisonError = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getQuestionnaireTemplates.pending, (state) => {
            state.templatesLoading = true;
            state.templatesError = null;
        });
        builder.addCase(getQuestionnaireTemplates.fulfilled, (state, action) => {
            state.templatesLoading = false;
            state.templates = action.payload;
        });
        builder.addCase(getQuestionnaireTemplates.rejected, (state, action) => {
            state.templatesLoading = false;
            state.templatesError = action.payload;
        });
        
        builder.addCase(getQuestionnaireTemplate.pending, (state) => {
            state.currentTemplateLoading = true;
            state.currentTemplateError = null;
        });
        builder.addCase(getQuestionnaireTemplate.fulfilled, (state, action) => {
            state.currentTemplateLoading = false;
            state.currentTemplate = action.payload;
        });
        builder.addCase(getQuestionnaireTemplate.rejected, (state, action) => {
            state.currentTemplateLoading = false;
            state.currentTemplateError = action.payload;
        });

        builder.addCase(createQuestionnaireTemplate.pending, (state) => {
            state.createTemplateLoading = true;
            state.createTemplateError = null;
        });
        builder.addCase(createQuestionnaireTemplate.fulfilled, (state, action) => {
            state.createTemplateLoading = false;
            state.templates.push(action.payload);
        });
        builder.addCase(createQuestionnaireTemplate.rejected, (state, action) => {
            state.createTemplateLoading = false;
            state.createTemplateError = action.payload;
        });

        builder.addCase(updateQuestionnaireTemplate.pending, (state) => {
            state.createTemplateLoading = true;
            state.createTemplateError = null;
        });
        builder.addCase(updateQuestionnaireTemplate.fulfilled, (state, action) => {
            state.createTemplateLoading = false;
            const index = state.templates.findIndex(t => t.id === action.payload.id);
            if (index !== -1) {
                state.templates[index] = action.payload;
            }
        });
        builder.addCase(updateQuestionnaireTemplate.rejected, (state, action) => {
            state.createTemplateLoading = false;
            state.createTemplateError = action.payload;
        });

        builder.addCase(deleteQuestionnaireTemplate.fulfilled, (state, action) => {
            state.templates = state.templates.filter(t => t.id !== action.payload);
        });

        builder.addCase(sendQuestionnaire.pending, (state) => {
            state.sendQuestionnaireLoading = true;
            state.sendQuestionnaireError = null;
        });
        builder.addCase(sendQuestionnaire.fulfilled, (state) => {
            state.sendQuestionnaireLoading = false;
        });
        builder.addCase(sendQuestionnaire.rejected, (state, action) => {
            state.sendQuestionnaireLoading = false;
            state.sendQuestionnaireError = action.payload;
        });

        builder.addCase(getSubmissionsByDomain.pending, (state) => {
            state.submissionsLoading = true;
            state.submissionsError = null;
        });
        builder.addCase(getSubmissionsByDomain.fulfilled, (state, action) => {
            state.submissionsLoading = false;
            state.submissions = action.payload;
        });
        builder.addCase(getSubmissionsByDomain.rejected, (state, action) => {
            state.submissionsLoading = false;
            state.submissionsError = action.payload;
        });

        builder.addCase(getAllSubmissions.pending, (state) => {
            state.submissionsLoading = true;
            state.submissionsError = null;
        });
        builder.addCase(getAllSubmissions.fulfilled, (state, action) => {
            state.submissionsLoading = false;
            state.submissions = action.payload;
        });
        builder.addCase(getAllSubmissions.rejected, (state, action) => {
            state.submissionsLoading = false;
            state.submissionsError = action.payload;
        });

        builder.addCase(getDomainStatistics.pending, (state) => {
            state.statisticsLoading = true;
            state.statisticsError = null;
        });
        builder.addCase(getDomainStatistics.fulfilled, (state, action) => {
            state.statisticsLoading = false;
            state.statistics = action.payload;
        });
        builder.addCase(getDomainStatistics.rejected, (state, action) => {
            state.statisticsLoading = false;
            state.statisticsError = action.payload;
        });

        
        builder.addCase(getSubmissionsComparison.pending, (state) => {
            state.comparisonLoading = true;
            state.comparisonError = null;
        });
        builder.addCase(getSubmissionsComparison.fulfilled, (state, action) => {
            state.comparisonLoading = false;
            state.comparison = action.payload;
        });
        builder.addCase(getSubmissionsComparison.rejected, (state, action) => {
            state.comparisonLoading = false;
            state.comparisonError = action.payload;
        });

        builder.addCase(getSchedulersByTemplate.pending, (state) => {
            state.schedulersLoading = true;
        });
        builder.addCase(getSchedulersByTemplate.fulfilled, (state, action) => {
            state.schedulersLoading = false;
            state.schedulers = action.payload;
        });
        builder.addCase(getSchedulersByTemplate.rejected, (state) => {
            state.schedulersLoading = false;
        });
        
        builder.addCase(updateQuestionnaireScheduler.pending, (state) => {
            state.schedulerActionLoading = true;
        });
        builder.addCase(updateQuestionnaireScheduler.fulfilled, (state, action) => {
            state.schedulerActionLoading = false;
            const index = state.schedulers.findIndex(s => s.id === action.payload.id);
            if (index !== -1) state.schedulers[index] = action.payload;
        });
        builder.addCase(updateQuestionnaireScheduler.rejected, (state) => {
            state.schedulerActionLoading = false;
        });
        
        builder.addCase(deleteQuestionnaireScheduler.pending, (state) => {
            state.schedulerActionLoading = true;
        });
        builder.addCase(deleteQuestionnaireScheduler.fulfilled, (state, action) => {
            state.schedulerActionLoading = false;
            state.schedulers = state.schedulers.filter(s => s.id !== action.payload);
        });
        builder.addCase(deleteQuestionnaireScheduler.rejected, (state) => {
            state.schedulerActionLoading = false;
        });
    },
});

export const { clearCurrentTemplate, clearStatistics, clearComparison } = questionnaireSlice.actions;

export default questionnaireSlice.reducer;